import React, { Component } from 'react';
import { observer } from 'mobx-react';
import {
  PushButton,
  VerticalProgress,
} from '../components';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import {
  Sound,
  Sounds,
} from '../../Sound';
import { WeighedMaterialType } from '../../../constants/MaterialModels';
import { toNumber } from '../../../utils/numbers';
import convertUnit from '../../../utils/unit-converter';
import {
  InventoryLabel,
  IconUnitLabel,
  TargetLabel,
  BalanceLabel,
  DeviceStatusLabel,
  CapacityLabel,
} from '../components/labels';
import { BinSwap } from '../components/BinSwap';
import { BinActivation } from '../components/BinActivation';
import {
  AnalogIconList,
  AnalogUnitList,
} from '../../../constants/DeviceModels';
import MaterialVisibilityChange from '../components/MaterialVisibilityChange';
import {
  getRunMode,
  isRunModeNewYork,
} from '../../../stores/batch/run-mode';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { CONTINUOUS_FEED_BUTTON_PRESS_DURATION } from '../../../constants/ContinuousPress';
const FEATURE_DEFER_BINS = isFeatureEnabled('defer_bins');
const FEATURE_SWAP_MATERIAL_MID_BATCH = isFeatureEnabled('swap_material_mid_batch');

@observer
class AggBin extends Component {
  handleShowSubstitutionSidebar = () => {
    const { device, material, batchStore, sidebarStore } = this.props;
    const scale = batchStore.devicesStore.getDeviceById(device.scaleId);
    const deviceStatus = batchStore.getDeviceStatus(device.id);

    const binWeight = deviceStatus ? deviceStatus.weight : 0;

    // Set the substitution data in batchStore
    batchStore.setMaterialSubstitutionData({
      originalMaterialId: material?.itemId,
      scaleId: scale?.id,
      currentWeight: binWeight,
    });

    // Open the sidebar
    sidebarStore.onToggle('materialSubstitution');
  };

  render() {
    const { device, batchStore, material, materialDetails, materialStore, settingsStore, onPush, onPushUp, holdFeed, finishFeed, isBinForMultiScale, deferFeed, isDeferFeedDisabled, isContinuousPressActive, hasBeenSubstituted, substitutionWeight } = this.props;
    const { configuration } = settingsStore;
    const active = batchStore.isOutputOn(device.gateOnePNo);
    const cn = active ? 'gate-open' : 'gate-close';
    const deviceStatus = batchStore.getDeviceStatus(device.id);
    const { status, target, error, unit, weight, materialCode } = deviceStatus;
    const runMode = getRunMode(configuration, batchStore);
    const isError = error ? 'error' : '';
    const md = materialDetails || {};
    const progress = weight > 0 ? 100 - (((target - weight) / target) * 100) : 0;
    const tolerance = target ? batchStore.getToleranceByPercent(md, target) : 0;
    const binBalance = materialStore.getDevicesBalance(material);
    const name = materialCode || (material ? materialStore.getMaterialName(material) : '');
    const materialType = md.type === WeighedMaterialType.FineAggregate ? 'fine-agg' : 'coarse-agg';
    const moisture = md.autoMoisture && md.autoMoistureAmount
      ? md.autoMoistureAmount
      : md.moistureAmount;
    const batchUnit = unit || md.itemBatchUnitSymbol;
    const inventoryUnit = material ? material.itemInventoryUnitSymbol : '';
    const scale = batchStore.devicesStore.getDeviceById(device.scaleId);
    const deviceUnit = scale?.unitId ? batchStore.getUnitById(scale.unitId) : batchUnit;
    const isDeferenceUnit = unit && target ? unit !== deviceUnit : false;
    let convertedTarget = target;
    if (hasBeenSubstituted) {
      convertedTarget -= substitutionWeight;
    }
    let convertedWeight = weight;
    const isMoreBins = md?.bins?.filter(v => !v.isPrimary && v.isActive).length === 1;
    const isSecondaryBin = md?.bins?.some(v => !v.isPrimary && v.binId === device.id);
    const isLinkedBin = md?.bins?.some(v => v.linkedBinId === device.id);
    const inputDisabled = batchStore.isInputDisabled(device.gateCloseStatusPNo) ? 'device-input-disabled' : '';
    const isBinDisabled = md.bins?.some(v => !v.isActive && (v.binId === device.id || v.linkedBinId === device.id)) ? 'bin-disabled' : '';
    const isBinDeferred = batchStore.isDeviceDeferred(device.scaleId, device.id);
    const binType = isSecondaryBin ? 'secondary' : 'primary';
    const noScaleError = !scale ? 'no-scale-error' : '';
    const materialStyle = materialDetails ? { width: 100 - progress + '%' } : { width: 0 };
    const isFeedMode = batchStore.isHoldFeedDevice(device.id)
      || (
        [DeviceStatus.FEED, DeviceStatus.JOG, DeviceStatus.HOLD, DeviceStatus.SETTLING, DeviceStatus.OVER].includes(deviceStatus.status)
        && batchStore.running.length
      );
    const noSwapBins = [DeviceStatus.FEED, DeviceStatus.JOG, DeviceStatus.SETTLING].includes(deviceStatus.status)
        && batchStore.running.length;
    const holdFeedIcon = batchStore.isHoldFeedDevice(device.id) ? 'play' : 'pause';
    const otherMaterials = materialStore.getMaterialsWithTheSameOutput(device.id);

    // Check if material substitution is available
    const canSubstituteMaterial = FEATURE_SWAP_MATERIAL_MID_BATCH && material && scale && batchStore.running.length &&
      [DeviceStatus.FEED, DeviceStatus.JOG, DeviceStatus.HOLD, DeviceStatus.SETTLING].includes(status);

    if (isDeferenceUnit) {
      convertedTarget = toNumber(convertUnit(target, deviceUnit, batchUnit), 0);
      convertedWeight = toNumber(convertUnit(weight, deviceUnit, batchUnit), 0);
    }

    return (
      <div id={`device-${device.id}`} className={`${cn} status-${status} device-gui device-gui--agg-bin device-${isError} ${inputDisabled} ${isBinDisabled} ${noScaleError} device-icon-${device.icon}`}>
        <Sound file={Sounds.alarm} play={isError} loop={true}/>
        <MaterialVisibilityChange materials={otherMaterials}
          material={material}
          onMaterialClick={m => materialStore.makeMaterialVisible(m, device.id)}
        />
        <DeviceStatusLabel
          name={name}
          description={device.description}
          status={status}
          title={`#${device.id} ${binType} ${inputDisabled} ${noScaleError}`}
          displayIcon={noScaleError}
        />
        <div className="device-label device-label-target">
          <TargetLabel target={convertedTarget} unit={batchUnit} icon={isSecondaryBin ? 'fa' : 'fa fa-crosshairs'} padZeros={scale?.graduation > 1}/>
          <BalanceLabel balance={convertedWeight} unit={batchUnit} padZeros={scale?.graduation > 1}/>
        </div>
        {!isLinkedBin && !noSwapBins && !isSecondaryBin && material && isMoreBins && (
          <BinSwap onChange={() => {
            materialStore.swapBins(device.id, material, md);
          }}/>
        )}
        {!isLinkedBin && !noSwapBins && isSecondaryBin && material && (
          <BinActivation activeClassName={isBinDisabled} onChange={() => {
            materialStore.toggleBin(device.id, material, md);
          }}/>
        )}

        {/* Material Substitution Button */}
        {canSubstituteMaterial && !isSecondaryBin && batchStore.isHoldFeedDevice(device.id) && (
          <button
            className="btn-on btn-batch btn-batch-orange btn-substitute-material"
            title="Substitute Material"
            onClick={this.handleShowSubstitutionSidebar}
          >
            <i className="fa fa-exchange"/>
          </button>
        )}

        <div className={`device-icon ${materialType}`} style={materialStyle} />
        <div className="device-icon body"/>
        <div className="device-icon gate gate-left"/>
        <div className="device-icon gate gate-right"/>
        <div className={`device-icon particle particle-${materialType}`}/>
        {(!isSecondaryBin || isBinForMultiScale) && !isRunModeNewYork(runMode) && !!isFeedMode && !batchStore.isHoldFeed && (
          <button className={'btn-on btn-batch btn-batch-red btn-hold-feed'}
            title={batchStore.isHoldFeedDevice(device.id) ? 'Start feed' : 'Hold feed'}
            disabled={isBinDeferred}
            onClick={holdFeed}>
            <i className={`fa fa-${holdFeedIcon}`}/>
          </button>
        )}
        {(!isSecondaryBin || isBinForMultiScale) && !isRunModeNewYork(runMode) && !!isFeedMode && batchStore.isHoldFeedDevice(device.id) && (
          <button className={'btn-on btn-batch btn-batch-red btn-finish-feed'}
            title="Finish feed"
            onClick={e => finishFeed(e, material)}>
            <i className={'fa fa-stop'}/>
          </button>
        )}
        {FEATURE_DEFER_BINS && !!isFeedMode && batchStore.isHoldFeedDevice(device.id) && !isRunModeNewYork(runMode) && (
          <button
            className="btn-on btn-batch btn-batch-red btn-batch-defer"
            title="Defer this material"
            onClick={deferFeed}
            disabled={isDeferFeedDisabled}
          >
            <i className="fa fa-forward"/>
          </button>
        )}
        {!!moisture &&
          <IconUnitLabel value={moisture} unit={AnalogUnitList['%']} icon={AnalogIconList.DROP}/>
        }
        {!!md.showBalance &&
          <InventoryLabel balance={binBalance} decimal={1} unit={inventoryUnit}/>
        }
        {!!md.showCapacity &&
          <CapacityLabel capacity={device.capacity} decimal={1} unit={inventoryUnit}/>
        }
        {(isBinDisabled && batchStore.running.length) || !device.gateOnePNo || (!configuration.manualFeed && batchStore.running.length) ? '' : (
          <PushButton port={batchStore.maintenanceMode && !configuration.reduceMaintenanceModeInventory ? null : device.gateOnePNo}
            onPush={onPush}
            onPushUp={onPushUp}
            disabled={isRunModeNewYork(runMode)}
            isContinuousPressActive={isContinuousPressActive}
            continuousPressDuration={CONTINUOUS_FEED_BUTTON_PRESS_DURATION}
            onRightClickHoldLabel={'Hold to keep open..'}
            onRightClickHoldEnd={onPush}
            rightClickHoldDuration={1250}
          />
        )}
        <VerticalProgress
          showPercent={true}
          showProgress={true}
          tolerance={tolerance}
          value={progress}
          active={active}
        />
      </div>
    );
  }
}

export default AggBin;
